import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { withRouter } from 'react-router-dom';
import { Row, ProgressBar } from 'react-bootstrap';
import { isEmpty, get } from 'lodash';
import { injectIntl } from 'react-intl';

import Image from '../../../shared/components/Image';
import { IMAGE_PROPS } from '../../../shared/utils/ImageUtils';
import { LEARNING_ASSET_DURATIONS } from '../../../shared/Constants';
import CopyLink from '../CopyLink';
import { renderCompletenessStatus } from './UserLearningAssetCard/UserLearningAssetCardUtils';


const ContentCard = (props) => {
    const {
        className,
        isReadOnly,
        content,
        content: { _id, name, thumbnailImageUrl, subtitle },
        isSlide,
        hideMetadata = false,
        overlayProps = {},
        cardImageProps,
        textProp = {},
        customStyles,
        history,
        customLink,
        cardNumber,
        fromExplore = false,
        pathsContext,
        type = 'COLLECTION',
        userSubscription = {},
        intl: { formatMessage },
    } = props;

    const [currentWidth, setCurrentWidth] = useState(window.outerWidth);

    useEffect(() => {
        const handleResize = () => setCurrentWidth(window.outerWidth);
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    });

    const _cardContainerClickHandler = () => {
        if (type === 'COLLECTION') {
            return history.push(customLink || `/collection/${content.permalink || content._id}`);
        }
        return history.push(customLink || `/assetlink/${_id}`);
    };

    const imageProps = cardImageProps || IMAGE_PROPS.collectionImageCloudinary;

    const _makeDetails = () => {
        let primaryDetail;
        let secondaryDetail;
        if (type === 'COLLECTION') {        
            if (content.content || content.contentIds) {
                const collectionCount = content.content ? content.content.length : content.contentIds.length;
                primaryDetail = (
                    <div className={'col-auto content-count'}>
                        <i className={'fal fa-book meta-icon-size meta-vertical-align'} />
                        <span className={'meta-vertical-align meta-space'}>{collectionCount} activities</span>
                    </div>
                );
            }
            if (content.durationInMinutes || content.totalTimeForCollection) {
                const collectionTime = content.durationInMinutes || content.totalTimeForCollection;
                const collectionText = collectionTime < 60 ? `${collectionTime} Mins` : `${Math.round(collectionTime / 60)} Hrs`;
                secondaryDetail = (
                    <div className={'col-auto content-time'}>
                        <i className={'fal fa-clock meta-icon-size meta-vertical-align'} />
                        <span className={'meta-vertical-align meta-space'}>{collectionText}</span>
                    </div>
                );
            }
        } else {
            const durationDisplay = get(LEARNING_ASSET_DURATIONS(), `[${content.durationId}].inMinutes`, content.duration);
            if (durationDisplay) {
                primaryDetail = (
                    <div className={'col-auto content-time'}>
                        <i className={'fal fa-clock meta-icon-size meta-vertical-align'} />
                        <span className={'meta-vertical-align meta-space'}>{durationDisplay} Mins</span>
                    </div>
                );
            }
            const copiedText = `${pathsContext.clientUrl}/assetlink/${_id}`;
            secondaryDetail = (
                <div className={'col-auto copy-link-container'}>
                    <CopyLink copyText={copiedText} successText={'Copied'} />
                </div>
            )
        }

        return (
            <div className={'asset-metadata-container col-12'}>
                <div className={'row details-container'}>
                    {primaryDetail}
                    {secondaryDetail}
                </div>
            </div>
        )
    }

    const _completePercent = (completedLearningAssets, totalLearningAssets) =>
        Math.round((completedLearningAssets / totalLearningAssets) * 100);

    let guideDisplay = {};
    let showProgressBar;
    let completionPercentage;

    if (!isEmpty(userSubscription)) {
        if (type === 'COLLECTION') {
            completionPercentage = _completePercent(userSubscription.completedLearningAssets, userSubscription.totalLearningAssets);
            showProgressBar = completionPercentage !== 0 && completionPercentage !== 100 && (userSubscription.status === 'IN_PROGRESS' || userSubscription.status === 'STARTED');
            imageProps.showProgressBar = showProgressBar;
            imageProps.completionPercentage = completionPercentage;
            if (userSubscription.status === 'COMPLETED' || completionPercentage === 100) {
                textProp.message = formatMessage({ id: 'HEADING_COMPLETE', defaultMessage: 'COMPLETE' }, {});
                textProp.backgroundHeight = '250';
                textProp.fontFamily = 'Avenir-Bold.ttf',
                textProp.fontWeight = '18',
                textProp.alignment = 'center',
                textProp.textY = '15',
                textProp.textColor = 'FFFFFF',
                textProp.backgroundWidth = '250',
                textProp.gravity = 'north'
                overlayProps.overlayId = 'teal';
                overlayProps.opacity = '60';
                overlayProps.gravity = 'north';
                overlayProps.dimension = { h: '45', w: '250' };
            }
        } else {
            guideDisplay = renderCompletenessStatus(userSubscription);
        }
    }

    const cardWithOutLink = (
        <Row key={_id} className={'content-container'}>
            <div className={`col-auto asset-left-display content-asset-left-display-${cardNumber} ${type.toLowerCase()}-left-display`}>
                <div className={`content-image-display ${type.toLowerCase()}-content-card`}>
                    {type === 'COLLECTION' && !isEmpty(userSubscription) && showProgressBar && (
                        <ProgressBar
                            key={content._id}
                            now={completionPercentage}
                            className={'user-collection-card-progress-bar'}
                        />
                    )}
                    <Image
                        overlay={overlayProps}
                        src={thumbnailImageUrl}
                        dimension={imageProps.dimension}
                        cloudinaryCrop={imageProps.cloudinaryCrop}
                        textProp={textProp}
                        className={'trackCardImage'}
                    />
                    {type === 'ACTIVITY' && !isEmpty(userSubscription) && !isEmpty(guideDisplay) && (
                        <div
                            className={cns(
                                'status',
                                { inProgress: userSubscription.status === 'IN_PROGRESS' },
                                { done: userSubscription.status === 'COMPLETED' }
                            )}
                        >
                            {guideDisplay}
                        </div>
                    )}
                </div>
            </div>
            <div className={`col content-details ${type.toLowerCase()}-details`}>
                <div className={'row'}>
                    <h5 className={cns(`content-title content-card-title-${cardNumber} col-12`)} onClick={!isReadOnly ? () => _cardContainerClickHandler() : undefined}>{name}</h5>
                    <div
                        className={`col-12 content-subtitle content-card-subtitle-${cardNumber}`}
                        onClick={!isReadOnly ? () => _cardContainerClickHandler() : undefined}
                    >
                        {subtitle}
                    </div>
                    <div className={cns('content-metadata-container', 'content-behaviors')} hidden={hideMetadata}>
                        {_makeDetails()}
                    </div>
                </div>
            </div>
        </Row>
    );

    return (
        <div
            className={cns(className, `content-card content-card-${cardNumber} ${type.toLowerCase()}-content-card`, {
                'col-lg-6': type === 'ACTIVITY' && currentWidth < 1441,
                'col-sm-12 col-md-12': currentWidth < 769,
                disabled: isReadOnly
            })}
            style={customStyles}
        >
            {cardWithOutLink}
        </div>
    );
};

ContentCard.propTypes = {
    cardImageProps: PropTypes.object,
    className: PropTypes.string,
    content: PropTypes.object,
    customLink: PropTypes.string,
    customStyles: PropTypes.object,
    disableLabel: PropTypes.bool,
    disableSubtitle: PropTypes.bool,
    hideMetadata: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    isSlide: PropTypes.bool,
    isVerbAdmin: PropTypes.bool,
    overlayProps: PropTypes.object,
    textProp: PropTypes.object,
    userCompany: PropTypes.object,
    history: PropTypes.object,
    menuOptions: PropTypes.array,
    cardNumber: PropTypes.number,
    fromExplore: PropTypes.bool,
    pathsContext: PropTypes.object,
    type: PropTypes.string,
    intl: PropTypes.object,
};

export const ContentCardComponent = ContentCard;

export default withRouter(injectIntl(ContentCard));
